import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@ublend-npm/aulaui-core';
import { getHomeViewRole } from '@app/selectors/home';
import { WidgetCard } from '../../common';
import QuickLinkTile from './QuickLinkTile';
import useHomeQuickLinks from '../useHomeQuickLinks';
import useBreakpoints from '@app/hooks/useBreakpoints';
import { toggleWidgetPreference } from '../../../../../../actions/home/widgetPreferences';

const QuickLinks: React.FC = () => {
  const currentRole = useSelector(getHomeViewRole);
  const dispatch = useDispatch();
  const { lg, xl } = useBreakpoints();
  const roleHrefKey = currentRole === 'student' ? 'learner' : 'educator';

  const { quickLinks, isLoading, hasError } = useHomeQuickLinks();
  const visibleLinks = quickLinks.filter((link) => link.href[roleHrefKey]);

  const handleHide = () => {
    dispatch(toggleWidgetPreference('home-quick-links-widget', true) as any);
  };

  const menuActions = [{ id: 'hide', label: 'Hide', onClick: handleHide }];

  const isCompact = lg && !xl;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [activePage, setActivePage] = useState(0);

  const columns = isCompact ? 4 : 2;
  const rows = 4;
  const itemsPerPage = columns * rows;

  const pages: typeof visibleLinks[] = [];
  for (let i = 0; i < visibleLinks.length; i += itemsPerPage) {
    pages.push(visibleLinks.slice(i, i + itemsPerPage));
  }

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || pages.length === 0) return;
    const pageWidth = el.clientWidth;
    const index = Math.round(el.scrollLeft / pageWidth);
    setActivePage(Math.min(Math.max(index, 0), pages.length - 1));
  };

  const scrollToPage = (index: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: el.clientWidth * index, behavior: 'smooth' });
  };

  useEffect(() => {
    setActivePage(0);
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: 0, behavior: 'auto' });
    }
  }, [visibleLinks.length]);

  if (isLoading || hasError || visibleLinks.length === 0) {
    return null;
  }

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          display: 'flex',
          gap: '32px',
          alignItems: 'flex-start',
          overflowX: 'auto',
          overflowY: 'visible',
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {pages.map((pageLinks, pageIndex) => (
          <Box
            key={pageIndex}
            sx={{
              flex: '0 0 100%',
              scrollSnapAlign: 'start',
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
              alignContent: 'start',
              columnGap: isCompact ? '8px' : '16px',
              rowGap: '16px',
            }}
          >
            {pageLinks.map((link) => (
              <QuickLinkTile
                key={link.key}
                link={link}
                role={currentRole}
                isCompact={isCompact}
              />
            ))}
          </Box>
        ))}
      </Box>

      {pages.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            mt: '16px',
          }}
        >
          {pages.map((_, index) => (
            <Box
              key={index}
              component="button"
              onClick={() => scrollToPage(index)}
              aria-label={`Go to page ${index + 1}`}
              sx={{
                width: index === activePage ? '10px' : '8px',
                height: index === activePage ? '10px' : '8px',
                borderRadius: '50%',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                backgroundColor:
                  index === activePage ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.25)',
                transition: 'all 0.2s ease',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {isCompact && (
        <style>{`
    div:has(> [data-widget-id='home-quick-links-widget']) {
      flex-wrap: wrap !important;
    }
    [data-widget-id='home-quick-links-widget'] {
      flex: 1 1 100% !important;
      max-width: 100% !important;
      order: 999 !important;
    }
  `}</style>
      )}
      <WidgetCard title="Quick links" menuActions={menuActions}>
        {content}
      </WidgetCard>
    </>
  );
};

export default QuickLinks;
